// Import the useRouter hook from Next.js
import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import Ticket from '@/components/Adstod/Ticket';
import { useRouter } from 'next/router';

import type { GetStaticPaths, GetStaticProps } from 'next';
import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { FooterQuery, NavbarQuery } from '../../../@types/generated';
import { queryFooter, queryNavbar } from '@/clients/queries';
import PageContainer from '@/components/PageContainer';

import Phone from '../../components/Icons/Phone';
import Internet from '../../components/Icons/Internet';
import TV from '../../components/Icons/TV';
import Invoice from '../../components/Icons/Invoice';
import LinePhone from '../../components/Icons/LinePhone';
import HandShake from '../../components/Icons/HandShake';
import Settings from '../../components/Icons/Settings';
import BackArrowRed from '../../components/Icons/BackArrowRed';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import LoadingIcon from '@/components/Icons/LoadingIcon';
import { Log } from '@/logging/datadog';

type Props = {
  navbar: NavbarQuery;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
};

const PostPage = ({ navbar, footer, radioSchedules }: Props) => {
  const [sortedData, setSortedData] = useState<CategoryType[]>([]);
  const [categorySortedData, setCategorySortedData] = useState<CategoryType[]>([]);
  const [activeCategory, setActiveCategory] = useState('Sjá allt');

  const categoryArray = [
    {
      id: 1,
      name: 'Farsími',
      icon: <Phone />,
      slug: 'farsimi',
      category: 'Voda_Farsimi',
    },
    {
      id: 2,
      name: 'Internet',
      icon: <Internet />,
      slug: 'internet',
      category: 'Vodaf_Internet',
    },
    {
      id: 3,
      name: 'Sjónvarp',
      icon: <TV />,
      slug: 'sjonvarp',
      category: 'Sj_nvarp',
    },
    {
      id: 4,
      name: 'Reikningar',
      icon: <Invoice />,
      slug: 'reikningar',
      category: 'Reikningar',
    },
    {
      id: 5,
      name: 'Heimasími',
      icon: <LinePhone />,
      slug: 'heimasimi',
      category: 'Voda_Heimasimi',
    },
    {
      id: 6,
      name: 'Stillingar',
      icon: <Settings />,
      slug: 'stillingar',
      category: 'Stillingar',
    },
    {
      id: 7,
      name: 'Fyrirtæki',
      icon: <HandShake />,
      slug: 'fyrirtaeki',
      category: 'Fyrirt_ki',
    },
  ];

  interface ItemType {
    // Define the structure of the details object here
    articleNumber: string;
    categoryGroups: {
      groupLabel: string;
      groupName: string;
      selectedCategories: {
        categoryLabel: string;
        categoryName: string;
        url: string;
      }[];
    }[];
    downVoteCount: number;
    id: string;
    lastPublishedDate: string;
    nextReviewDate: string | null;
    summary: string;
    title: string;
    upVoteCount: number;
    url: string;
    urlName: string;
    viewCount: number;
    viewScore: number;
    // Add more fields as needed
  }

  interface CategoryType {
    name: string;
    items: ItemType[];
  }

  interface CategoryArrayType extends Array<CategoryType> {}

  // Initialize the category in the map if it doesn't exist

  // Use the useRouter hook to access the route parameters
  const router = useRouter();
  // Extract the slug from the route parameters
  const { slug, id } = router.query;

  const matchedCategory = categoryArray.find((category) => category.slug === slug);

  //const articleRefs = useRef({});
  const articleRefs = useRef<{ [key: string]: HTMLLIElement | undefined }>({});

  useEffect(() => {
    if (typeof id === 'string' && articleRefs.current && articleRefs.current[id as keyof typeof articleRefs.current]) {
      articleRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [id, sortedData]);

  useEffect(() => {
    const sortArticles = (data: ItemType[]) => {
      const _activeCategories: CategoryArrayType = [];

      data.forEach((item) => {
        // Assuming each item has a categoryGroups array and we're interested in the first categoryGroup
        const categoryName = item.categoryGroups[0].selectedCategories[0].categoryLabel;

        // Initialize the category in the map if it doesn't exist
        for (let i = 0; i < _activeCategories.length; i++) {
          if (_activeCategories[i].name === categoryName) {
            _activeCategories[i].items.push(item);
            return;
          }
        }
        _activeCategories.push({ name: categoryName, items: [item] });
      });

      return _activeCategories;
    };

    const fetchData = async () => {
      if (!matchedCategory?.category) return;

      try {
        const credentials = process.env.NEXT_PUBLIC_SPURT_SVARAD_CREDENTIALS || '';
        const headers = {
          Authorization: `Basic ${credentials}`,
        };

        const endpointBase = `${process.env.NEXT_PUBLIC_SPURT_SVARAD_ENDPOINT}${matchedCategory.category}%22%7D&pageNumber=`;

        const allArticles: Array<ItemType> = [];

        // Adding in an extra page, in case the questions ever exceed 40.
        let cont = true;
        let pageNo = 1;
        while (cont) {
          const response = await fetch(`${endpointBase}${pageNo}`, { headers });
          const json = await response.json();
          if (Array.isArray(json.articles)) {
            allArticles.push(...json.articles);
          }
          if (!json.nextPageUrl) {
            cont = false;
          }
          pageNo++;
        }

        const _sortedArticles = sortArticles(allArticles);
        setSortedData(_sortedArticles);
        setCategorySortedData(_sortedArticles);
      } catch (error) {
        Log.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [matchedCategory?.category]);

  const handleRouteBack = () => {
    router.push(`/adstod`);
  };

  const handleClickCategory = (category: string) => {
    setActiveCategory(category);

    if (category === 'Sjá allt') {
      setCategorySortedData(sortedData);
      return;
    }

    const _categorySortedData = sortedData.filter((item) => item.name === category);

    setCategorySortedData(_categorySortedData);
  };

  const ticketShouldOpen = (ticketId: string) => {
    if (id) {
      if (id === ticketId) {
        return true;
      }
    }
    return false;
  };

  const title = matchedCategory?.name ? `Sýn | Aðstoð | ${matchedCategory.name}` : 'Aðstoð';

  const metaTags = {
    title,
    description: 'Aðstoð',
    url: `https://www.syn.is/frettir/${matchedCategory?.name}`,
    keywords: 'aðstoð,spurt og svarað,hjálp,spurningar',
  };

  return (
    <>
      <PageContainer title={title} metaTags={metaTags} navbar={navbar} footer={footer} radioSchedules={radioSchedules}>
        <div
          className="col-span-full mt-80 sm:mx-4"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '50px',
              marginTop: '100px',
              width: '1016px',
              maxWidth: '100vw',
              maxHeight: '496px',
              justifyContent: 'center',
              alignItems: 'start',
            }}
            className="px-12 sm:px-0"
          >
            <div
              onClick={() => handleRouteBack()}
              style={{
                marginBottom: '4rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <BackArrowRed />
              <div
                style={{
                  marginLeft: '8px',
                  color: '#F14157',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: '700',
                  lineHeight: 'normal',
                }}
              >
                <p>Allir flokkar</p>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginBottom: '24px' }}>
              <div style={{ marginRight: '8px', alignContent: 'center' }}>{matchedCategory?.icon}</div>
              <div>
                <h1
                  style={{
                    fontSize: '40px',
                    fontStyle: 'normal',
                    fontWeight: '700',
                    lineHeight: 'normal',
                    color: '#4A4D4E',
                  }}
                >
                  {matchedCategory?.name}
                </h1>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {sortedData.length > 0 ? (
                <div className="flex flex-wrap gap-12">
                  <div
                    onClick={() => handleClickCategory('Sjá allt')}
                    style={{
                      color: activeCategory === 'Sjá allt' ? 'white' : '#F14157',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '700',
                      padding: '8px 16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '40px',
                      cursor: 'pointer',
                      border: '1px solid #F14157',
                      marginBottom: '10px',
                      backgroundColor: activeCategory === 'Sjá allt' ? '#F14157' : 'transparent',
                    }}
                  >
                    <p>Sjá allt</p>
                  </div>
                  {sortedData.map((category) => (
                    <div key={category.name}>
                      <h3
                        onClick={() => handleClickCategory(category.name)}
                        style={{
                          color: activeCategory === category.name ? 'white' : '#F14157',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: '700',
                          padding: '8px 16px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px',
                          borderRadius: '40px',
                          cursor: 'pointer',
                          marginBottom: '10px',
                          border: '1px solid #F14157',
                          background: activeCategory === category.name ? '#F14157' : 'transparent',
                        }}
                      >
                        {category.name}
                      </h3>
                    </div>
                  ))}
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div style={{ width: '100%', backgroundColor: 'white', display: 'flex', justifyContent: 'center' }}>
            {categorySortedData.length > 0 ? (
              <div style={{ width: '808px', paddingBottom: '4rem' }}>
                {categorySortedData.map((category) => (
                  <div key={category.name} className="px-12">
                    <h3
                      style={{
                        fontSize: '32px',
                        fontStyle: 'normal',
                        fontWeight: '700',
                        lineHeight: 'normal',
                        marginTop: '56px',
                        color: '#4A4D4E',
                      }}
                    >
                      {category.name}
                    </h3>
                    <ul>
                      {category.items.map((item) => (
                        <li
                          key={item.id}
                          ref={(el) => {
                            if (el) articleRefs.current[item.id] = el;
                          }}
                        >
                          <Ticket ticketInfo={item} ticketOpenURL={ticketShouldOpen(item.id)}></Ticket>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-center">
                  <LoadingIcon />
                </div>
              </div>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const footer = await queryFooter(apolloClient);
    const navbar = await queryNavbar(apolloClient);

    return {
      props: {
        navbar: navbar.data,
        footer: footer.data,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      { params: { slug: 'internet' } },
      { params: { slug: 'heimasimi' } },
      { params: { slug: 'farsimi' } },
      { params: { slug: 'sjonvarp' } },
      { params: { slug: 'reikningar' } },
      { params: { slug: 'stillingar' } },
      { params: { slug: 'fyrirtaeki' } },
    ],
    fallback: false,
  };
};

export default PostPage;
